/**
 * NOAKS: Kombinationsbewusste Variantenauswahl
 * ---------------------------------------------------------------------------
 * Problem
 *   Bei duenn besetzten Variantenmatrizen (Beispiel "ALLE NOAKS BAGS":
 *   5 Farben x 7 Groessen = 35 Kombinationen, aber nur 15 existieren) bot das
 *   Theme alle Kombinationen an. Shopifys `value.available` heisst naemlich nur
 *   "dieser Wert kommt in irgendeiner lieferbaren Variante vor" - nicht "in
 *   Kombination mit dem bereits Gewaehlten". Kunden konnten damit Artikel
 *   auswaehlen, die es gar nicht gibt.
 *
 *   Verschaerfend: Shopify korrigiert eine unmoegliche Kombination NICHT von
 *   selbst. Wird die Section mit einer nicht existierenden Kombination
 *   angefordert, kommt gar keine Variante zurueck (empirisch geprueft am
 *   Produkt: PINK + XS -> leeres data-selected-variant). Die Korrektur muss
 *   also im Browser passieren, BEVOR die Anfrage rausgeht.
 *
 * Loesung (zwei Baustellen)
 *   1) Hauptauswahl (<variant-selects>): Ein change-Listener in der
 *      CAPTURE-Phase auf document laeuft garantiert vor dem eigenen Listener
 *      von VariantSelects (der haengt in der Bubble-Phase am Element). Wird
 *      eine Option geaendert, ziehen wir alle nachfolgenden Optionen auf einen
 *      Wert nach, den es mit der neuen Auswahl wirklich gibt. Shopify bekommt
 *      damit immer eine existierende Kombination.
 *
 *   2) Bundle-/Quantity-Break-Zeilen: Dort haengt pro Packung ein eigenes Paar
 *      Dropdowns, das serverseitig ungefiltert ALLE Werte enthielt. Wir
 *      begrenzen die Folge-Dropdowns je Zeile auf real existierende und
 *      lieferbare Kombinationen.
 *
 * Die erste Option (Farbe) bleibt bewusst immer vollstaendig waehlbar - der
 * Kunde landet nie in einer Sackgasse, die Groesse springt stattdessen nach.
 *
 * Datenquelle ist die von product-variant-picker.liquid gerenderte Matrix
 * (`[{o:[Optionswerte], a:lieferbar, id:VariantenID}, ...]`).
 */
(function () {
  'use strict';

  var MATRIX_SELECTOR = 'script[data-noaks-variant-matrix]';
  var GROUP_SELECTOR = '.product-form__input';
  var QTY_ROW_SELECTOR = '.lumin-product-block-qty-break-variant';

  /** Liest die Variantenmatrix aus dem naechstgelegenen Produkt-Container. */
  function readMatrix(node) {
    var scope = node && node.closest ? node.closest('.product') : null;
    var script = (scope || document).querySelector(MATRIX_SELECTOR);
    if (!script) return null;

    try {
      var parsed = JSON.parse(script.textContent);
      return Array.isArray(parsed) && parsed.length ? parsed : null;
    } catch (error) {
      console.warn('[noaks] Variantenmatrix nicht lesbar:', error);
      return null;
    }
  }

  /**
   * Liefert fuer Optionsposition `index` alle Werte, die es zusammen mit
   * `prefix` (den Werten der vorhergehenden Optionen) wirklich gibt.
   * `existing` = Variante existiert, `inStock` = zusaetzlich lieferbar.
   */
  function allowedValues(matrix, index, prefix) {
    var existing = [];
    var inStock = [];

    matrix.forEach(function (variant) {
      // Defensiv: fehlerhafte Eintraege ueberspringen statt die ganze
      // Auswahl lahmzulegen (Plattform-Standard "defensives Rendern").
      if (!variant || !Array.isArray(variant.o)) return;

      for (var i = 0; i < prefix.length; i++) {
        if (variant.o[i] !== prefix[i]) return;
      }

      var value = variant.o[index];
      if (value == null) return;

      if (existing.indexOf(value) === -1) existing.push(value);
      if (variant.a && inStock.indexOf(value) === -1) inStock.push(value);
    });

    return { existing: existing, inStock: inStock };
  }

  /* ---------------------------------------------------------------------
   * 1) Hauptauswahl
   * ------------------------------------------------------------------ */

  /**
   * Baut aus den Options-Gruppen eine einheitliche Schnittstelle - egal ob
   * die Gruppe als Radio-Buttons, Swatches oder Dropdown gerendert ist.
   */
  function optionGroups(root) {
    var groups = [];

    Array.prototype.forEach.call(root.querySelectorAll(GROUP_SELECTOR), function (element) {
      var select = element.querySelector('select');

      if (select) {
        groups.push({
          element: element,
          value: function () {
            return select.value;
          },
          select: function (value) {
            var match = Array.prototype.filter.call(select.options, function (option) {
              return option.value === value;
            })[0];
            if (!match) return false;

            Array.prototype.forEach.call(select.options, function (option) {
              option.removeAttribute('selected');
            });
            match.disabled = false;
            match.setAttribute('selected', 'selected');
            select.value = value;
            return true;
          },
        });
        return;
      }

      var radios = Array.prototype.slice.call(element.querySelectorAll('input[type="radio"]'));
      if (!radios.length) return;

      groups.push({
        element: element,
        value: function () {
          var checked = radios.filter(function (radio) {
            return radio.checked;
          })[0];
          return checked ? checked.value : null;
        },
        select: function (value) {
          var match = radios.filter(function (radio) {
            return radio.value === value;
          })[0];
          if (!match) return false;

          // Der Server hat den Button evtl. fuer die ALTE Auswahl gesperrt.
          // Mit der neuen Auswahl ist er gueltig - Sperre aufheben, sonst
          // traegt VariantSelects ihn nicht in die Anfrage ein.
          match.disabled = false;
          match.checked = true;
          return true;
        },
      });
    });

    return groups;
  }

  /**
   * Zieht nach einer Auswahl alle NACHFOLGENDEN Optionen auf gueltige Werte
   * nach. Vorhergehende Optionen bleiben unangetastet - deshalb ist die erste
   * Option (Farbe) immer frei waehlbar.
   */
  function correctMainSelection(root, matrix, changedInput) {
    var groups = optionGroups(root);
    if (groups.length < 2) return;

    var changedElement = changedInput.closest(GROUP_SELECTOR);
    var changedIndex = -1;

    groups.forEach(function (group, index) {
      if (group.element === changedElement) changedIndex = index;
    });
    if (changedIndex === -1) return;

    var chosen = groups.map(function (group) {
      return group.value();
    });

    for (var i = changedIndex + 1; i < groups.length; i++) {
      var options = allowedValues(matrix, i, chosen.slice(0, i));
      if (!options.existing.length) break;

      // Aktuelle Wahl bleibt stehen, wenn es sie mit der neuen Auswahl gibt.
      if (options.existing.indexOf(chosen[i]) !== -1) continue;

      var replacement = options.inStock.length ? options.inStock[0] : options.existing[0];
      if (groups[i].select(replacement)) chosen[i] = replacement;
    }
  }

  // Capture-Phase: laeuft vor dem Bubble-Listener von <variant-selects>,
  // damit die korrigierte Auswahl schon in der Section-Anfrage steckt.
  document.addEventListener(
    'change',
    function (event) {
      var input = event.target;
      if (!input || !input.closest) return;

      var root = input.closest('variant-selects');
      if (!root) return;

      var matrix = readMatrix(root);
      if (!matrix) return;

      correctMainSelection(root, matrix, input);
    },
    true
  );

  /* ---------------------------------------------------------------------
   * 2) Bundle-/Quantity-Break-Zeilen
   * ------------------------------------------------------------------ */

  var syncing = false;

  /** Schreibt die Optionsliste eines Dropdowns neu (nur gueltige Werte). */
  function renderOptions(select, values, selected) {
    var signature = values.join(' ');
    if (select.dataset.noaksRendered === signature && select.value === selected) return false;

    select.innerHTML = '';
    values.forEach(function (value) {
      var option = document.createElement('option');
      option.value = value;
      option.textContent = value;
      if (value === selected) option.setAttribute('selected', 'selected');
      select.appendChild(option);
    });

    select.value = selected;
    select.dataset.noaksRendered = signature;
    return true;
  }

  /**
   * Begrenzt in einer Packungs-Zeile die Folge-Dropdowns auf Kombinationen,
   * die es wirklich gibt. Nicht lieferbare fallen raus - ein Bundle mit einem
   * ausverkauften Artikel liesse sich ohnehin nicht bestellen.
   */
  function syncQtyRow(row, matrix, changedSelect) {
    var selects = Array.prototype.slice.call(row.querySelectorAll('select'));
    if (selects.length < 2) return false;

    var startIndex = changedSelect ? selects.indexOf(changedSelect) + 1 : 1;
    if (startIndex < 1) startIndex = 1;

    var corrected = false;

    for (var i = startIndex; i < selects.length; i++) {
      var prefix = selects.slice(0, i).map(function (select) {
        return select.value;
      });

      var options = allowedValues(matrix, i, prefix);
      var usable = options.inStock.length ? options.inStock : options.existing;
      if (!usable.length) continue;

      var previous = selects[i].value;
      var keep = usable.indexOf(previous) !== -1 ? previous : usable[0];

      renderOptions(selects[i], usable, keep);
      if (keep !== previous) corrected = true;
    }

    return corrected;
  }

  /**
   * Erstinitialisierung der Zeilen. `emit` loest einmalig ein change-Event
   * aus, damit Lumins Preis- und Varianten-ID-Logik den korrigierten Stand
   * uebernimmt - sonst zeigte der Block einen Preis, der nicht zur Auswahl passt.
   */
  function syncAllQtyRows(scope, emit) {
    var rows = (scope || document).querySelectorAll(QTY_ROW_SELECTOR);
    if (!rows.length) return;

    syncing = true;
    try {
      Array.prototype.forEach.call(rows, function (row) {
        var matrix = readMatrix(row);
        if (!matrix) return;

        var corrected = syncQtyRow(row, matrix, null);
        if (corrected && emit) {
          var selects = row.querySelectorAll('select');
          var last = selects[selects.length - 1];
          if (last) last.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    } finally {
      syncing = false;
    }
  }

  // Auch hier Capture-Phase, damit Lumins eigener Handler bereits die
  // korrigierten Werte sieht und Preis/Varianten-IDs stimmen.
  document.addEventListener(
    'change',
    function (event) {
      if (syncing) return;

      var select = event.target;
      if (!select || !select.closest || select.tagName !== 'SELECT') return;

      var row = select.closest(QTY_ROW_SELECTOR);
      if (!row) return;

      var matrix = readMatrix(row);
      if (!matrix) return;

      syncing = true;
      try {
        syncQtyRow(row, matrix, select);
      } finally {
        syncing = false;
      }
    },
    true
  );

  /* ---------------------------------------------------------------------
   * Nach DOM-Austausch neu anwenden
   * ------------------------------------------------------------------ */

  // product-info.js ersetzt die Quantity-Break-Bloecke nach jedem
  // Variantenwechsel komplett durch frisches Server-HTML (ungefiltert).
  // Ein Observer zieht die Filterung danach zuverlaessig nach - unabhaengig
  // vom Timing der dortigen 500-ms-Verzoegerung.
  var pending = false;

  function scheduleResync() {
    if (pending) return;
    pending = true;
    requestAnimationFrame(function () {
      pending = false;
      syncAllQtyRows(document, true);
    });
  }

  function watch() {
    syncAllQtyRows(document, false);

    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;

        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;

          if (
            (node.matches && node.matches(QTY_ROW_SELECTOR)) ||
            (node.querySelector && node.querySelector(QTY_ROW_SELECTOR))
          ) {
            scheduleResync();
            return;
          }
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watch);
  } else {
    watch();
  }
})();
